/**
 * Mock chart note template types for the Medical Record "Chart note" modal.
 * Each template defines a reason/template type with its own fields and placeholder content
 * so the modal UI and content structure update dynamically by selection.
 */

export interface ChartNoteTemplateField {
  id: string;
  label: string;
  placeholder: string;
  required?: boolean;
  multiline?: boolean;
}

export interface ChartNoteTemplate {
  id: string;
  label: string;
  description: string;
  fields: ChartNoteTemplateField[];
  /** Default/placeholder values for demo; keys match field ids. */
  demoValues: Record<string, string>;
}

export const CHART_NOTE_TEMPLATES: ChartNoteTemplate[] = [
  {
    id: 'follow-up',
    label: 'Follow-up',
    description: 'Routine follow-up documentation or check-in note.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Weekly check-in', required: true },
      { id: 'context', label: 'Context / Summary', placeholder: 'Brief context for this follow-up...', multiline: true, required: true },
      { id: 'review', label: 'What was reviewed', placeholder: 'Topics or items reviewed...', multiline: true },
      { id: 'plan', label: 'Plan / Next steps', placeholder: 'Follow-up plan or next steps...', multiline: true },
    ],
    demoValues: {
      title: 'Weekly check-in',
      context: 'Client presented for scheduled weekly follow-up. Mood improved since last session. No acute concerns.',
      review: 'Reviewed mood log and sleep hygiene. Client reported 2 nights of improved sleep. Discussed work stress and coping strategies.',
      plan: 'Continue weekly therapy. Client to practice relaxation exercises 3x/week. PHQ-9 at next visit. Next session in 7 days.',
    },
  },
  {
    id: 'phone-call',
    label: 'Phone call note',
    description: 'Documentation of a phone call or telehealth check-in.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Phone check-in', required: true },
      { id: 'reason', label: 'Reason for call', placeholder: 'Why the client or provider initiated the call...', multiline: true, required: true },
      { id: 'summary', label: 'Summary of call', placeholder: 'Key points discussed and outcome...', multiline: true, required: true },
      { id: 'plan', label: 'Follow-up', placeholder: 'Any follow-up or next steps...', multiline: true },
    ],
    demoValues: {
      title: 'Phone check-in',
      reason: 'Client called to discuss increased anxiety after a family conflict. Requested brief support and coping strategies.',
      summary: 'Spoke with client for ~15 minutes. Validated feelings, reviewed grounding techniques and problem-solving steps. Client reported feeling calmer by end of call. No safety concerns.',
      plan: 'Scheduled earlier in-person session this week. Client will use crisis line if needed before then.',
    },
  },
  {
    id: 'crisis',
    label: 'Crisis note',
    description: 'Documentation of a crisis contact or safety-related encounter.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Crisis contact', required: true },
      { id: 'presentation', label: 'Presentation / Triggers', placeholder: 'How the crisis presented and any triggers...', multiline: true, required: true },
      { id: 'assessment', label: 'Risk assessment', placeholder: 'Brief risk assessment and safety considerations...', multiline: true, required: true },
      { id: 'intervention', label: 'Intervention and disposition', placeholder: 'What was done and outcome...', multiline: true, required: true },
      { id: 'plan', label: 'Follow-up plan', placeholder: 'Safety plan updates, next steps, referrals...', multiline: true },
    ],
    demoValues: {
      title: 'Crisis contact',
      presentation: 'Client contacted practice in distress after argument with partner. Reported passive SI without intent or plan. Denied means access.',
      assessment: 'Low acute risk. No intent, plan, or means. Good insight and willingness to use coping skills. Protective factors: strong support, previous safety plan on file.',
      intervention: 'Phone session ~20 min. Reviewed safety plan, encouraged use of crisis line. Client agreed to remove means from home. No hospitalization indicated.',
      plan: 'Earlier appointment scheduled. Safety plan reviewed and copy sent to client. Follow-up call in 24 hours.',
    },
  },
  {
    id: 'general',
    label: 'General chart note',
    description: 'General clinical documentation not tied to an appointment.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Clinical note', required: true },
      { id: 'content', label: 'Content', placeholder: 'Enter note content...', multiline: true, required: true },
    ],
    demoValues: {
      title: 'Clinical note',
      content: 'Documentation of coordination with PCP regarding medication. Client consented to release. Summary faxed. No changes to current regimen.',
    },
  },
  // Service-specific templates (structure only; content not pre-filled)
  {
    id: 'family',
    label: 'Family Therapy Progress Note',
    description: 'Template for family therapy sessions. Documents family system, dynamics, and treatment progress.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Family session', required: true },
      { id: 'membersPresent', label: 'Family members present', placeholder: 'List who attended...', multiline: true },
      { id: 'presentingConcerns', label: 'Presenting concerns today', placeholder: '', multiline: true },
      { id: 'dynamicsObserved', label: 'Family dynamics and interaction patterns', placeholder: '', multiline: true },
      { id: 'progressGoals', label: 'Progress toward treatment goals', placeholder: '', multiline: true },
      { id: 'plan', label: 'Plan / Next steps', placeholder: '', multiline: true },
    ],
    demoValues: {},
  },
  {
    id: 'couples',
    label: 'Couples Therapy Progress Note',
    description: 'Template for couples therapy sessions. Documents relationship focus and progress.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Couples session', required: true },
      { id: 'attendance', label: 'Attendance', placeholder: 'Both partners / one absent', multiline: false },
      { id: 'topicsDiscussed', label: 'Primary topic(s) discussed', placeholder: '', multiline: true },
      { id: 'interactionPattern', label: 'Couple interaction and communication', placeholder: '', multiline: true },
      { id: 'relationshipProgress', label: 'Progress on relationship goals', placeholder: '', multiline: true },
      { id: 'plan', label: 'Plan / Next steps', placeholder: '', multiline: true },
    ],
    demoValues: {},
  },
  {
    id: 'iop-group',
    label: 'IOP Group Session Note',
    description: 'Template for IOP group therapy sessions. Documents group module, participation, and skill progress.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. IOP Group — Day X of Y', required: true },
      { id: 'moduleTopic', label: 'Group module / topic', placeholder: 'e.g. DBT Skills, CBT, Process Group', multiline: false },
      { id: 'clientReport', label: 'Client self-report and symptom check-in', placeholder: '', multiline: true },
      { id: 'participation', label: 'Participation and skills practiced', placeholder: '', multiline: true },
      { id: 'riskLevel', label: 'Risk level and step-down readiness', placeholder: '', multiline: true },
      { id: 'plan', label: 'Plan / Coordination', placeholder: '', multiline: true },
    ],
    demoValues: {},
  },
  {
    id: 'group',
    label: 'Group Therapy Progress Note',
    description: 'Template for group therapy sessions (non-IOP). Documents group process and member participation.',
    fields: [
      { id: 'title', label: 'Title', placeholder: 'e.g. Group session', required: true },
      { id: 'groupTopic', label: 'Group topic / theme', placeholder: '', multiline: false },
      { id: 'processObservations', label: 'Group process and observations', placeholder: '', multiline: true },
      { id: 'participationNotes', label: 'Individual participation notes', placeholder: '', multiline: true },
      { id: 'plan', label: 'Plan / Next steps', placeholder: '', multiline: true },
    ],
    demoValues: {},
  },
];

/**
 * Templates shown in the Medical Record "Chart note" modal only (standalone notes not tied to an appointment).
 * Progress note templates (follow-up, phone-call, crisis, family, couples, iop-group, group) are used exclusively in appointments.
 */
export const CHART_NOTE_STANDALONE_TEMPLATES: ChartNoteTemplate[] = CHART_NOTE_TEMPLATES.filter(
  (t) => t.id === 'general',
);

/** Map appointment service type display text to chart note template id for defaulting. */
const SERVICE_TYPE_TO_TEMPLATE_ID: Record<string, string> = {
  'family therapy': 'family',
  'couples therapy': 'couples',
  'group therapy (iop)': 'iop-group',
  'iop group': 'iop-group',
  'group therapy': 'group',
};

export function getChartNoteTemplateById(id: string): ChartNoteTemplate | undefined {
  return CHART_NOTE_TEMPLATES.find((t) => t.id === id);
}

export function getChartNoteTemplateIdByServiceType(serviceType: string): string | undefined {
  const key = serviceType.trim().toLowerCase();
  return SERVICE_TYPE_TO_TEMPLATE_ID[key] ?? Object.entries(SERVICE_TYPE_TO_TEMPLATE_ID).find(([k]) => key.includes(k))?.[1];
}
